import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

const greenBlink = keyframes`
  0% { background-color: #43861D; }
  50% { background-color: #96DC6F; }
  100% {background-color: #43861D; }
`;
const orangeBlink = keyframes`
  0% { background-color: #DE7C13; }
  50% { background-color: #F2B879; }
  100% {background-color: #DE7C13; }
`;
const redBlink = keyframes`
  0% { background-color: #CE332A; }
  50% { background-color: #EC908B; }
  100% {background-color: #CE332A; }
`;
const blueBlink = keyframes`
  0% { background-color: #2A78CD; }
  50% { background-color: #80B1E5; }
  100% {background-color: #2A78CD; }
`;
const whiteBlink = keyframes`
  0% { background-color: #fff; }
  50% { background-color: #D8D8D8; }
  100% {background-color: #fff; }
`;
const darkBlink = keyframes`
  0% { border-color: #161718; }
  50% { border-color: #D8D8D8; }
  100% {border-color: #161718; }
`;
const lightBlink = keyframes`
0% { border-color: #eee; }
50% { border-color: #454546; }
100% {border-color: #eee; }
`;
const disabledBlink = keyframes`
  0% { background-color: #D8D8D8; }
  50% { background-color: #EEEEEE; }
  100% {background-color: #D8D8D8; }
`;

export type ButtonProps = Omit<
  MuiButtonProps,
  'nonce' | 'color' | 'disableElevation' | 'disableFocusRipple' | 'disableRipple' | 'classes' | 'variant'
> & {
  blinking?: boolean;
  variant?: 'success' | 'warning' | 'alert' | 'info' | 'white' | 'dark' | 'light';
};

const ButtonBase: React.FC<ButtonProps> = ({ blinking, variant, ...props }) => (
  <MuiButton {...props} disableElevation disableRipple disableFocusRipple />
);

export const Button = styled(ButtonBase)<ButtonProps>(({ theme, variant = 'success', blinking, disabled }) => ({
  minWidth: '150px',
  color: theme.palette.common.white,

  ...(variant === 'success' && {
    backgroundColor: '#43861D',
    '&:hover': {
      backgroundColor: '#55a628',
    },
    ...(blinking && {
      animation: `${greenBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'warning' && {
    backgroundColor: '#DE7C13',
    '&:hover': {
      backgroundColor: '#df8f38',
    },
    ...(blinking && {
      animation: `${orangeBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'alert' && {
    backgroundColor: '#CE332A',
    '&:hover': {
      backgroundColor: '#d84a42',
    },
    ...(blinking && {
      animation: `${redBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'info' && {
    backgroundColor: '#2A78CD',
    '&:hover': {
      backgroundColor: '#4a8fdb',
    },
    ...(blinking && {
      animation: `${blueBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'white' && {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: '#e1e1e1',
    },
    border: `2px solid ${theme.palette.text.primary}`,
    ...(blinking && {
      animation: `${whiteBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'dark' && {
    backgroundColor: 'transparent',
    border: `2px solid #161718`,
    color: theme.palette.text.primary,
    ...(blinking && {
      animation: `${darkBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(variant === 'light' && {
    backgroundColor: 'transparent',
    border: `2px solid ${theme.palette.common.white}`,
    ...(blinking && {
      animation: `${lightBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),

  ...(disabled && {
    backgroundColor: '#D8D8D8',
    color: theme.palette.action.disabled,
    borderColor: theme.palette.action.disabled,
    ...(blinking && {
      animation: `${disabledBlink} 1s infinite ${theme.transitions.easing.easeInOut}`,
    }),
  }),
}));
